import { useEffect, useState } from "react";
import { MailIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { ProblemDetailsAlert } from "@/components/ProblemDetailsAlert";
import { useApiAction } from "@/hooks/useApiAction";
import { cn } from "@/lib/utils";

type ClientRole = components["schemas"]["ClientRole"];
type ClientInviteWithTokenResponse =
  components["schemas"]["ClientInviteWithTokenResponse"];

const ROLE_OPTIONS: { value: ClientRole; description: string }[] = [
  { value: "Admin", description: "Manages team, keys, webhooks, settings" },
  { value: "Developer", description: "Manages keys + webhooks only" },
  { value: "Member", description: "Everyday access, no keys or settings" },
];

interface CreateClientInviteDialogProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (invite: ClientInviteWithTokenResponse) => void;
}

export function CreateClientInviteDialog({
  clientId,
  open,
  onOpenChange,
  onCreated,
}: CreateClientInviteDialogProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ClientRole>("Developer");
  const [validationError, setValidationError] = useState<string | null>(null);

  const action = useApiAction(
    (
      body: components["schemas"]["CreateClientInviteRequest"],
    ): Promise<{
      data?: ClientInviteWithTokenResponse;
      error?: components["schemas"]["ProblemDetails"];
    }> =>
      api.POST("/v1/client/invites", {
        body,
      }),
  );

  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("Developer");
      setValidationError(null);
      action.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setValidationError(null);

    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      setValidationError("Enter a valid email address.");
      return;
    }

    const invite = await action.run({ email: trimmed, role });
    if (invite) {
      onCreated(invite);
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Invite a member</DialogTitle>
            <DialogDescription>
              They'll get a link they can open and accept with Google sign-in. The
              OAuth-verified email must match exactly.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Work email</Label>
              <div className="relative">
                <MailIcon className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="dev@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-8"
                  autoFocus
                  disabled={action.pending}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Role">
                {ROLE_OPTIONS.map((opt) => {
                  const selected = role === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setRole(opt.value)}
                      disabled={action.pending}
                      className={cn(
                        "flex items-center gap-3 rounded-full border px-3 py-1.5 text-left text-sm transition",
                        selected
                          ? "border-primary/60 bg-primary/15 text-foreground ring-primary/40 ring-2"
                          : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                      )}
                    >
                      <span
                        className={cn(
                          "shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
                          selected
                            ? "border-transparent bg-primary text-primary-foreground"
                            : "border-border bg-muted text-foreground",
                        )}
                      >
                        {opt.value}
                      </span>
                      <span
                        className={cn(
                          "text-xs leading-snug",
                          selected ? "text-foreground" : "",
                        )}
                      >
                        {opt.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {validationError && (
              <p className="text-destructive text-sm">{validationError}</p>
            )}

            <ProblemDetailsAlert
              problem={action.error}
              fallback="Couldn't create invite."
            />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={action.pending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={action.pending}>
              {action.pending && <Spinner className="size-4" />}
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
